import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EntityCombobox } from '@/components/custom/entity-combobox';
import { providerPickerConfig, locationPickerConfig } from '@/components/custom/entity-pickers';
import { APPOINTMENT_STATUS, APPOINTMENT_STATUS_LABELS } from './hooks/use-schedule';

export interface AppointmentFiltersState {
  providerIds: string[];
  locationIds: string[];
  statuses: number[];
  search: string;
}

interface AppointmentFiltersProps {
  filters: AppointmentFiltersState;
  onChange: (filters: AppointmentFiltersState) => void;
}

export function AppointmentFilters({
  filters,
  onChange,
}: AppointmentFiltersProps): React.ReactElement {
  const activeFilterCount =
    filters.providerIds.length + filters.locationIds.length + filters.statuses.length;

  const handleStatusChange = (value: string): void => {
    if (value === 'all') {
      onChange({ ...filters, statuses: [] });
    } else {
      const statusNum = Number(value);
      const newStatuses = filters.statuses.includes(statusNum)
        ? filters.statuses.filter((s) => s !== statusNum)
        : [...filters.statuses, statusNum];
      onChange({ ...filters, statuses: newStatuses });
    }
  };

  const clearFilters = (): void => {
    onChange({
      providerIds: [],
      locationIds: [],
      statuses: [],
      search: '',
    });
  };

  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b border-border/50 bg-background/95 backdrop-blur-xs">
      {/* Provider Filter — multi-select entity picker (preloaded + searchable).
          Width is fixed on this wrapper because the picker's own root is w-full. */}
      <div className="w-[180px] shrink-0">
        <EntityCombobox
          multiple
          config={providerPickerConfig}
          loadStrategy="preload"
          value={filters.providerIds}
          onChange={(ids) => onChange({ ...filters, providerIds: ids })}
          placeholder="All Providers"
          searchPlaceholder="Search providers…"
          badgeOverflow="scroll"
          className="min-h-8 text-xs"
        />
      </div>

      {/* Location Filter — multi-select entity picker. */}
      <div className="w-[180px] shrink-0">
        <EntityCombobox
          multiple
          config={locationPickerConfig}
          loadStrategy="preload"
          value={filters.locationIds}
          onChange={(ids) => onChange({ ...filters, locationIds: ids })}
          placeholder="All Locations"
          searchPlaceholder="Search locations…"
          badgeOverflow="scroll"
          className="min-h-8 text-xs"
        />
      </div>

      {/* Status Filter */}
      <Select
        value={filters.statuses.length === 0 ? 'all' : String(filters.statuses[0])}
        onValueChange={handleStatusChange}
      >
        <SelectTrigger className="w-[120px] h-8 text-xs">
          <SelectValue placeholder="Status">
            {filters.statuses.length === 0
              ? 'All Statuses'
              : filters.statuses.length === 1
                ? APPOINTMENT_STATUS_LABELS[filters.statuses[0]!]
                : `${filters.statuses.length} Statuses`}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Statuses</SelectItem>
          <SelectItem value={String(APPOINTMENT_STATUS.BOOKED)}>Booked</SelectItem>
          <SelectItem value={String(APPOINTMENT_STATUS.PENDING)}>Pending</SelectItem>
          <SelectItem value={String(APPOINTMENT_STATUS.ARRIVED)}>Arrived</SelectItem>
          <SelectItem value={String(APPOINTMENT_STATUS.FULFILLED)}>Fulfilled</SelectItem>
          <SelectItem value={String(APPOINTMENT_STATUS.CANCELLED)}>Cancelled</SelectItem>
          <SelectItem value={String(APPOINTMENT_STATUS.NOSHOW)}>No Show</SelectItem>
        </SelectContent>
      </Select>

      {/* Clear Filters Button */}
      {activeFilterCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearFilters}
          className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5 mr-1" />
          Clear
        </Button>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search patient..."
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          className="pl-9 pr-8 h-8 w-[240px] text-sm"
        />
        {filters.search && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => onChange({ ...filters, search: '' })}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring/50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
